import { 
  StoreDisplayGroup, 
  ApiResponse, 
  PaginatedResponse, 
  StoreDisplayGroupFilters, 
  PaginationParams,
  BoundShelf
} from '../types/storeDisplayGroup';
import { 
  mockStoreDisplayGroups, 
  mockStoreCodes,
  getStoreDisplayGroupByCode,
  getBoundShelvesForGroup,
  getStoreCodeOptions
} from '../mock/storeDisplayGroupData';
import { validateFilters } from '../utils/validation';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import { createErrorInfo, logError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

// In-memory storage for demo purposes
let storeDisplayGroups = [...mockStoreDisplayGroups];

/**
 * Service class for Store Display Group operations
 * Supports both Mock data and real API calls
 */
export class StoreDisplayGroupService {
  private static instance: StoreDisplayGroupService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): StoreDisplayGroupService {
    if (!StoreDisplayGroupService.instance) {
      StoreDisplayGroupService.instance = new StoreDisplayGroupService();
    }
    return StoreDisplayGroupService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    StoreDisplayGroupService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return StoreDisplayGroupService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get paginated list of store display groups with filters
   * @param filters Filter criteria
   * @param pagination Pagination parameters
   * @returns Promise with paginated store display group list
   */
  public async getStoreDisplayGroups(
    filters: StoreDisplayGroupFilters = {},
    pagination: PaginationParams = { page: 1, pageSize: 10 }
  ): Promise<ApiResponse<PaginatedResponse<StoreDisplayGroup>>> {
    try {
      // Validate input parameters
      if (!validateFilters(filters)) {
        return createErrorResponse('请求参数有误，请检查后重试', 'INVALID_PARAMS');
      }

      if (StoreDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        let filteredGroups = [...storeDisplayGroups];

        // Apply store code filter
        if (filters.storeCode) {
          filteredGroups = filteredGroups.filter(group => group.storeCode === filters.storeCode);
        }

        // Apply display group code filter
        if (filters.displayGroupCode) {
          filteredGroups = filteredGroups.filter(group => group.displayGroupCode === filters.displayGroupCode);
        }

        // Apply search filter
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredGroups = filteredGroups.filter(group => 
            group.name.toLowerCase().includes(searchTerm) || 
            group.code.toLowerCase().includes(searchTerm) ||
            group.storeCode.toLowerCase().includes(searchTerm)
          );
        }

        // Apply sorting
        if (pagination.sortBy) {
          filteredGroups.sort((a, b) => {
            let aValue: any = a[pagination.sortBy as keyof StoreDisplayGroup];
            let bValue: any = b[pagination.sortBy as keyof StoreDisplayGroup];
            
            // Handle date sorting
            if (pagination.sortBy === 'createTime' || pagination.sortBy === 'updateTime') {
              aValue = new Date(aValue).getTime();
              bValue = new Date(bValue).getTime();
            }
            
            if (pagination.sortOrder === 'desc') {
              return aValue > bValue ? -1 : 1;
            }
            return aValue > bValue ? 1 : -1;
          });
        } else {
          // Default sort by updateTime desc
          filteredGroups.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        }

        // Apply pagination
        const total = filteredGroups.length;
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const tableData = filteredGroups.slice(startIndex, endIndex);

        return createPaginatedResponse(tableData, pagination.page, pagination.pageSize, total);
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.STORE_DISPLAY_GROUPS.PAGE,
          {
            ...filters,
            currentPage: pagination.page,
            pageSize: pagination.pageSize,
            sortBy: pagination.sortBy,
            sortOrder: pagination.sortOrder
          }
        );
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取门店陈列分组列表失败');
      logError(errorInfo, 'getStoreDisplayGroups');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Get store display group by code
   * @param code Store display group code
   * @returns Promise with store display group details
   */
  public async getStoreDisplayGroupByCode(code: string): Promise<ApiResponse<StoreDisplayGroup>> {
    try {
      if (StoreDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const displayGroup = getStoreDisplayGroupByCode(code);
        
        if (!displayGroup) {
          return createErrorResponse('门店陈列分组不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(displayGroup);
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.STORE_DISPLAY_GROUPS.QUERY_BY_CODE + `?code=${code}`);
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取门店陈列分组详情失败');
      logError(errorInfo, 'getStoreDisplayGroupByCode');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  public async getOptionByUserStore(division: string): Promise<ApiResponse<any>> {
    try {
      // Call real API
        const response = await httpClient.get(API_ENDPOINTS.STORE_DISPLAY_GROUPS.OPTION_BY_STORE + `?division=${division}`);
        return response.data;
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取门店陈列分组详情失败');
      logError(errorInfo, 'getStoreDisplayGroupByCode');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

}

// Export singleton instance
export default StoreDisplayGroupService.getInstance();